import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'

const fired = new Set<string>()
let notifId = 1000

let audio: HTMLAudioElement | null = null
let userHasInteracted = false
let interactionListenerAttached = false

function ensureUserInteraction() {
    if (userHasInteracted || interactionListenerAttached) return
    interactionListenerAttached = true
    const onInteraction = () => {
        userHasInteracted = true
        // Pre-load audio on first interaction
        if (!audio) {
            audio = new Audio('/sounds/beep-boop.mp3')
            audio.load()
        }
        document.removeEventListener('click', onInteraction)
        document.removeEventListener('touchstart', onInteraction)
    }
    document.addEventListener('click', onInteraction)
    document.addEventListener('touchstart', onInteraction)
}

/**
 * Play beep-boop sound and wait for completion
 * Returns a promise that resolves when sound finishes playing
 */
function playSound(): Promise<void> {
    return new Promise((resolve) => {
        ensureUserInteraction()
        if (!audio) {
            audio = new Audio('/sounds/beep-boop.mp3')
        }

        // Only play if user has interacted (browser autoplay policy)
        if (!userHasInteracted) {
            resolve()
            return
        }

        // Setup completion handler
        const onEnded = () => {
            audio?.removeEventListener('ended', onEnded)
            audio?.removeEventListener('error', onError)
            resolve()
        }

        const onError = () => {
            audio?.removeEventListener('ended', onEnded)
            audio?.removeEventListener('error', onError)
            resolve() // Resolve even on error
        }

        audio.addEventListener('ended', onEnded)
        audio.addEventListener('error', onError)

        // Set timeout fallback (beep-boop is ~1 second)
        const timeoutId = setTimeout(() => {
            onEnded()
        }, 2000)

        audio.play().catch(() => {
            clearTimeout(timeoutId)
            resolve() // Resolve on play error too
        })
    })
}

const alertCallbacks: ((title: string, body: string) => void)[] = []

export function onAlert(callback: (title: string, body: string) => void) {
    alertCallbacks.push(callback)
    return () => {
        const idx = alertCallbacks.indexOf(callback)
        if (idx >= 0) alertCallbacks.splice(idx, 1)
    }
}

function showInAppAlert(title: string, body: string) {
    alertCallbacks.forEach(cb => cb(title, body))
}

function nextId() {
    return notifId++
}

export async function requestNotificationPermission(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
        const { display } = await LocalNotifications.requestPermissions()
        return display === 'granted'
    }
    // Web fallback
    if (!('Notification' in window)) return false
    if (Notification.permission === 'granted') return true
    if (Notification.permission === 'denied') return false
    return (await Notification.requestPermission()) === 'granted'
}

export async function canNotify(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
        const { display } = await LocalNotifications.checkPermissions()
        return display === 'granted'
    }
    return 'Notification' in window && Notification.permission === 'granted'
}

/**
 * Fire a notification once per key.
 * Sequence: beep-boop sound → in-app alert → native notification
 * No-op if already fired or no permission.
 */
export async function notify(key: string, title: string, body: string): Promise<void> {
    if (fired.has(key)) return
    fired.add(key)

    // Play sound first, wait for completion
    await playSound()

    // Show in-app alert
    showInAppAlert(title, body)

    if (Capacitor.isNativePlatform()) {
        try {
            await LocalNotifications.schedule({
                notifications: [{
                    id: nextId(),
                    title,
                    body,
                    smallIcon: 'ic_stat_icon_config_sample',
                    iconColor: '#1976d2',
                }],
            })
        }
        catch (e) {
            console.warn('[notify]', e)
        }
        return
    }

    // Web fallback
    if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, { body })
    }
}

export function resetNotification(key: string) {
    fired.delete(key)
}
export function resetAllNotifications() {
    fired.clear()
}
